import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import noop from '../utils/noop';
import getChildInstanceOf from '../common/utils/get-child-instance-of';
import Render from '../render';

function PopoverTrigger({ children }) {
  return children;
}

function PopoverContent({ children }) {
  return children;
}

class Popover extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
    };

    this.animationContainerRef = React.createRef();
    this.showPopover = this.showPopover.bind(this);
    this.hidePopover = this.hidePopover.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { isHidden } = this.props;

    if (prevProps.isHidden !== isHidden) {
      this.hidePopover();
    }
  }

  showPopover() {
    const { isHidden } = this.props;

    if (!isHidden) {
      this.setState({
        isVisible: true,
      });
    }
  }

  hidePopover() {
    this.setState({
      isVisible: false,
    });
  }

  render() {
    const {
      children,
      width,
      height,
      origin,
      place,
      enterDelay,
      exitDelay,
      onEnter,
      onExit,
      hovering,
    } = this.props;
    const { isVisible } = this.state;
    const trigger = getChildInstanceOf(PopoverTrigger, children);
    const content = getChildInstanceOf(PopoverContent, children);
    const fadeInDuration = 175;

    return (
      <div className="ws-popover" onMouseEnter={this.showPopover} onMouseLeave={this.hidePopover}>
        <div className="ws-popover__trigger">{trigger}</div>
        <CSSTransition
          nodeRef={this.animationContainerRef}
          in={isVisible}
          timeout={{
            enter: enterDelay + fadeInDuration,
            exit: exitDelay + fadeInDuration,
          }}
          classNames="ws-fade-in-out"
          unmountOnExit
          onEnter={onEnter}
          onExit={onExit}
        >
          {(state) => (
            <div
              ref={this.animationContainerRef}
              className={`ws-popover__content ws-popover__content--${place} ws-popover__content--${origin}`}
              style={{
                height,
                width,
                transitionDelay: `${state === 'entering' ? enterDelay : exitDelay}ms`,
                pointerEvents: `${hovering ? 'auto' : 'none'}`,
                whiteSpace: width !== 'auto' ? 'normal' : 'nowrap',
              }}
            >
              {content}
              <Render if={hovering}>
                <div className={`ws-popover__hovering ws-popover__hovering--${place}`} />
              </Render>
            </div>
          )}
        </CSSTransition>
      </div>
    );
  }
}

Popover.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  origin: PropTypes.oneOf(['start', 'center', 'end']),
  place: PropTypes.oneOf(['above', 'bellow', 'left', 'right']),
  enterDelay: PropTypes.number,
  exitDelay: PropTypes.number,
  onEnter: PropTypes.func,
  onExit: PropTypes.func,
  children: PropTypes.node.isRequired,
  hovering: PropTypes.bool,
  isHidden: PropTypes.bool,
};

Popover.defaultProps = {
  width: 'auto',
  height: 'auto',
  origin: 'center',
  place: 'above',
  enterDelay: 0,
  exitDelay: 300,
  onEnter: noop,
  onExit: noop,
  hovering: false,
  isHidden: false,
};

export { Popover, PopoverTrigger, PopoverContent };
